import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Setup axios interceptor for auth
const getStoredToken = () => localStorage.getItem("session_token");
axios.interceptors.request.use(config => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = getStoredToken();
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      localStorage.removeItem("session_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, token) => {
    if (token) localStorage.setItem("session_token", token);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    localStorage.removeItem("session_token");
    setUser(null);
  };

  const hasPermission = (perm) => {
    if (!user) return false;
    // Strict check against the explicit permissions list — super_admin alone
    // is no longer a blanket grant, so we can have role=super_admin users
    // (project admins) who lack RIOS access.
    return Array.isArray(user.permissions) && user.permissions.includes(perm);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
